import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismCard,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { task, type TaskInstance } from 'ember-concurrency';
import { t } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import SkeletonTableIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-table.svg?raw';
import HotelBenchmarkInsightButtonGroupChartKpis from 'lumen/components/hotel/benchmark-insight/button-group-chart-kpis';
import HotelBenchmarkInsightChartsChannelChart from 'lumen/components/hotel/benchmark-insight/charts/channel-chart';
import HotelBenchmarkInsightDropdownsDropdownOccupancyView from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import {
  OCCUPANCY_VIEWS,
  type OccupancyView,
} from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import type { SectionHeaderBreadcrumb } from 'lumen/components/hotel/benchmark-insight/sections/section-header-breadcrumbs';
import HotelBenchmarkInsightSectionsSectionHeaderBreadcrumbs from 'lumen/components/hotel/benchmark-insight/sections/section-header-breadcrumbs';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import HotelBenchmarkInsightTablesBeniTable from 'lumen/components/hotel/benchmark-insight/tables/beni-table';
import type {
  BeniBreakdown,
  BeniMeta,
  BeniMetaComparisonSet,
} from 'lumen/models';
import {
  type BeniBreakdownType,
  BREAKDOWNS,
  type ChannelHierarchyItem,
  isChannelHierarchyItem,
  type Kpi,
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type {
  DateRangeType,
  SegmentItem,
} from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import PmsMappingValueBenchmark from 'lumen/utils/datamodels/mapping/pms-mapping-value-benchmark';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';

import styles from './section-channel.module.css';

interface HotelBenchmarkInsightSectionsSectionChannelSectionChannelSignature {
  Args: {
    breakdownType: BeniBreakdownType;
    breakdownsByComparisonSet: Record<ComparisonSet, BeniBreakdown[]>;
    changeSelectedOccupancyView: (view: OccupancyView) => void;
    channel: ChannelHierarchyItem | null;
    hierarchyChannelTask: TaskInstance<MappingCategoryBenchmark> | undefined;
    isExpanded: boolean;
    isLoading: boolean;
    meta: BeniMeta;
    onUpdateChartReference?: (chart: Highcharts.Chart) => void;
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    pickupTranslation: string;
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    primaryComparisonSetType: ComparisonSet;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    secondaryComparisonSetType: ComparisonSet;
    selectedKpiChannel: Kpi;
    selectedOccupancyView: OccupancyView;
    selectedRange: DateRangeType;
    setChannel: (channel: ChannelHierarchyItem | null) => void;
    setSelectedKpiChannel: (kpi: Kpi) => void;
    showSkeleton: boolean;
    showSkeletonAnimation: boolean;
  };
  Blocks: {
    expandButton: [];
  };
}

export default class HotelBenchmarkInsightSectionsSectionChannelSectionChannel extends Component<HotelBenchmarkInsightSectionsSectionChannelSectionChannelSignature> {
  @service('benchmark-insight/beni-table-service')
  declare benchmarkInsightBeniTableService: Services['benchmark-insight/beni-table-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  breakdown = BREAKDOWNS.CHANNEL;

  exportChannelTableTask = task({}, async (): Promise<void> => {
    const { selectedRange } = this.args;
    const { rootCategory, columns, rows, breakdown } = this;

    await this.benchmarkInsightBeniTableService.exportTableTask.perform({
      columns,
      rows,
      breakdown,
      rootCategory,
      dateRange: selectedRange,
    });

    this.usageAnalytics.track('Table Exported', {
      eventTrigger: 'channel',
      product: 'benchmark-insight',
    });
  });

  get breadcrumbs(): SectionHeaderBreadcrumb[] {
    const breadcrumbs: {
      id: string | null;
      isClickable: boolean;
      isTopLevel?: boolean;
      label: string;
    }[] = [];

    const { rootCategory } = this;

    if (!rootCategory || rootCategory.isRootCategory) {
      return breadcrumbs;
    }

    breadcrumbs.push({
      id: null,
      label: this.intl.t('benchmark_insight.breakdown.channel'),
      isClickable: true,
      isTopLevel: true,
    });

    breadcrumbs.push(
      ...rootCategory.getParents().map((category) => {
        return {
          id: category.name,
          label: category.getDisplayName(this.intl, this.errorTracking) ?? '',
          isClickable: true,
        };
      }),
    );

    breadcrumbs.push({
      id: rootCategory.id,
      label: rootCategory.getDisplayName(this.intl, this.errorTracking) ?? '',
      isClickable: false,
    });

    return breadcrumbs;
  }

  get channelItems(): SegmentItem[] {
    return this.flattenedCategories.map((category) => {
      const categoryName = category.getDisplayName(
        this.intl,
        this.errorTracking,
      );

      const id = category.id;

      return {
        id,
        value: categoryName ?? undefined,
        isParent: category.parentId === null,
        depth: category.depth,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = this.benchmarkInsightBeniTableService.getColumns({
      primaryComparisonSetType: this.args.primaryComparisonSetType,
      secondaryComparisonSetType: this.args.secondaryComparisonSetType,
      ownComparisonSetMeta: this.ownComparisonSetMeta,
      primaryComparisonSetMeta: this.primaryComparisonSetMeta,
      secondaryComparisonSetMeta: this.secondaryComparisonSetMeta,
      breakdown: this.breakdown,
      dateAggregation: DATE_AGGREGATION.DAY,
      selectedRange: this.args.selectedRange,
      referencePeriod: this.args.referencePeriod,
      visibleKpis: this.visibleKpis,
    });

    return columns;
  }

  /**
   * Checks whether the hierarchy data is available then flattens all the segments in the root BenchmarkMappingCategory object into a list
   * @return { Array } : List of segments
   */
  get flattenedCategories(): MappingCategoryBenchmark[] {
    const { rootCategory } = this;

    if (!rootCategory) {
      return [];
    }

    const flattenedCategories: MappingCategoryBenchmark[] = [];

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const traverseCategories = (category: MappingCategoryBenchmark) => {
      flattenedCategories.push(category);
      category.children.forEach(traverseCategories);
    };

    traverseCategories(rootCategory);

    return flattenedCategories;
  }

  get isDropdownOccupancyViewVisible(): boolean {
    return this.args.selectedKpiChannel === KPIS.OCCUPANCY;
  }

  get isExportButtonDisabled(): boolean {
    return (
      this.args.showSkeleton ||
      this.exportChannelTableTask.isRunning ||
      this.benchmarkInsightFeatures.isBenchmarkInsightLight
    );
  }

  get isFiltering(): boolean {
    return this.args.channel !== null;
  }

  get ownComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      BENI_COMPARISON_SETS.OWN,
    );
  }

  get ownComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta } = this.args;

    if (!meta) {
      return null;
    }

    return meta.getMetaForComparisonSet(BENI_COMPARISON_SETS.OWN);
  }

  get primaryComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet, primaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      primaryComparisonSetType,
    );
  }

  get primaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, primaryComparisonSetType } = this.args;

    if (!meta || !primaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(primaryComparisonSetType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rootCategory() {
    const { hierarchyChannelTask, channel } = this.args;

    if (!hierarchyChannelTask?.value) {
      return null;
    }

    const rootCategoryChannel = hierarchyChannelTask.value;

    if (channel === null) {
      return rootCategoryChannel;
    }

    return PmsMappingValueBenchmark.findCategoryById(
      rootCategoryChannel.children,
      channel,
    );
  }

  get rows(): SegmentItem[] {
    const rows = this.benchmarkInsightBeniTableService.getRows(
      this.ownComparisonSetBreakdowns,
      this.primaryComparisonSetBreakdowns,
      this.secondaryComparisonSetBreakdowns,
      this.channelItems,
      this.breakdown,
    );

    const nestedRows = this.nestRows(rows);

    const totalRow = rows.find((row) => row.isTotalRow);
    if (!totalRow) {
      return nestedRows;
    }

    return [...nestedRows, totalRow];
  }

  get secondaryComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet, secondaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      secondaryComparisonSetType,
    );
  }

  get secondaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, secondaryComparisonSetType } = this.args;

    if (!meta || !secondaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(secondaryComparisonSetType);
  }

  get showMix(): boolean {
    const { selectedKpiChannel, selectedOccupancyView } = this.args;

    return (
      selectedOccupancyView === OCCUPANCY_VIEWS.MIX &&
      selectedKpiChannel === KPIS.OCCUPANCY
    );
  }

  get visibleKpis(): Kpi[] {
    const allKpis = Object.values(KPIS);

    return this.showMix
      ? allKpis.filter((kpi) => kpi !== KPIS.OCCUPANCY)
      : allKpis.filter((kpi) => kpi !== KPIS.OCCUPANCY_MIX);
  }

  getComparisonSetBreakdowns(
    breakdownsByComparisonSet: Partial<Record<ComparisonSet, BeniBreakdown[]>>,
    comparisonSet: ComparisonSet,
  ): BeniBreakdown[] {
    if (!breakdownsByComparisonSet) {
      return [];
    }

    const comparisonSetBreakdowns = breakdownsByComparisonSet[comparisonSet];
    if (!comparisonSetBreakdowns) {
      return [];
    }

    return comparisonSetBreakdowns;
  }

  nestRows(rows: SegmentItem[]): SegmentItem[] {
    const { channel } = this.args;
    const { rootCategory } = this;

    if (!rootCategory) {
      return rows;
    }

    // Recursive function to map the hierarchy of mapping categories to the nested rows.
    const mapHierarchy = (
      category: MappingCategoryBenchmark,
      rowsByCategoryId: Record<string, SegmentItem>,
    ): SegmentItem => {
      return {
        ...rowsByCategoryId[category.id],
        children: category.children.map((child) =>
          mapHierarchy(child, rowsByCategoryId),
        ),
      };
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const rowsByCategoryId: Record<string, SegmentItem> = Object.fromEntries(
      rows.map((row) => [row.categoryId, row]),
    );

    let rootCategoryToUse = rootCategory;

    if (channel) {
      // Recursively find the category in the hierarchy that matches the selected segment.
      const findCategory = (
        category: MappingCategoryBenchmark,
      ): MappingCategoryBenchmark | null => {
        if (category.id === channel) {
          return category;
        }

        for (const child of category.children) {
          const foundCategory = findCategory(child);
          if (foundCategory) {
            return foundCategory;
          }
        }

        return null;
      };

      const foundCategory = findCategory(rootCategory);
      if (foundCategory) {
        rootCategoryToUse = foundCategory;
      }
    }

    const rootRow = mapHierarchy(rootCategoryToUse, rowsByCategoryId);

    if (!channel) {
      // We don't want to show the root category in the table, so we return its children.
      return rootRow['children'] ?? [];
    }

    return [rootRow];
  }

  @action
  onClickBreadcrumb(breadcrumbId: string | null): void {
    const { setChannel } = this.args;

    if (breadcrumbId === null) {
      setChannel(null);
      return;
    }

    if (!isChannelHierarchyItem(breadcrumbId)) {
      return;
    }

    setChannel(breadcrumbId);
  }

  <template>
    <PrismCard class="h-full overflow-hidden" data-test-id="section-channel">
      <:header>
        <div class="flex items-center">
          <HotelBenchmarkInsightSectionsSectionHeaderBreadcrumbs
            @breadcrumbs={{this.breadcrumbs}}
            @onClickBreadcrumb={{this.onClickBreadcrumb}}
            @title={{t "benchmark_insight.breakdown.channel"}}
          />
        </div>
      </:header>

      <:headerAfter>
        <div class="flex justify-center gap-100">
          {{#if this.benchmarkInsightFeatures.showExportButtons}}
            <div>
              <PrismButton
                @buttonType="secondary"
                @iconBefore="export"
                @isDisabled={{this.isExportButtonDisabled}}
                @isLoading={{this.exportChannelTableTask.isRunning}}
                @label={{t "general.export_group.export"}}
                @onClick={{this.exportChannelTableTask.perform}}
                class="text-nowrap"
                data-test-id="section-channel-export-button"
              />

              {{#if this.benchmarkInsightFeatures.isBenchmarkInsightLight}}
                <PrismTooltip
                  @text={{this.benchmarkInsightFeatures.beniLightSegmentationTooltipText}}
                />
              {{/if}}
            </div>
          {{/if}}

          {{yield to="expandButton"}}
        </div>
      </:headerAfter>

      <:content>
        <div class="flex flex-col gap-400">
          <div>
            <div class="mb-400">
              <HotelBenchmarkInsightButtonGroupChartKpis
                @breakdownType={{@breakdownType}}
                @changeSelectedKpi={{@setSelectedKpiChannel}}
                @dataTestId="section-channel-kpi-selector"
                @selectedKpi={{@selectedKpiChannel}}
              />

              <div class={{styles.view-buttons}}>
                {{#if this.isDropdownOccupancyViewVisible}}
                  <div class={{styles.line}}></div>

                  <div class={{styles.dropdown}}>
                    <HotelBenchmarkInsightDropdownsDropdownOccupancyView
                      @changeSelectedOccupancyView={{@changeSelectedOccupancyView}}
                      @selectedOccupancyView={{@selectedOccupancyView}}
                    />
                  </div>
                {{/if}}
              </div>
            </div>

            <HotelBenchmarkInsightChartsChannelChart
              @channel={{@channel}}
              @isFiltering={{this.isFiltering}}
              @onUpdateChartReference={{@onUpdateChartReference}}
              @ownComparisonSetBreakdowns={{this.ownComparisonSetBreakdowns}}
              @ownComparisonSetMeta={{this.ownComparisonSetMeta}}
              @pickupTranslation={{@pickupTranslation}}
              @primaryComparisonSetBreakdowns={{this.primaryComparisonSetBreakdowns}}
              @primaryComparisonSetMeta={{this.primaryComparisonSetMeta}}
              @referencePeriod={{@referencePeriod}}
              @rootCategory={{this.rootCategory}}
              @secondaryComparisonSetBreakdowns={{this.secondaryComparisonSetBreakdowns}}
              @secondaryComparisonSetMeta={{this.secondaryComparisonSetMeta}}
              @selectedKpiChannel={{@selectedKpiChannel}}
              @selectedOccupancyView={{@selectedOccupancyView}}
              @selectedRange={{@selectedRange}}
              @setChannel={{@setChannel}}
              @showMix={{this.showMix}}
              @showSkeleton={{@showSkeleton}}
              @showSkeletonAnimation={{@showSkeletonAnimation}}
              @visibleKpis={{this.visibleKpis}}
            />
          </div>

          {{#if @isExpanded}}
            <div class="flex flex-col gap-400">
              <p class="text-400--bold">
                {{t "general.details"}}
              </p>

              {{#if @showSkeleton}}
                <HotelBenchmarkInsightSkeleton
                  @imageHtml={{SkeletonTableIllustration}}
                  @showAnimation={{@showSkeletonAnimation}}
                />
              {{else}}
                <div class="rounded-200">
                  <HotelBenchmarkInsightTablesBeniTable
                    @columns={{this.columns}}
                    @dataTestId="section-channel-table"
                    @rows={{this.rows}}
                  />
                </div>
              {{/if}}
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
